import { Box } from '@mui/material';
import React, { useContext, useState } from 'react';

import { COLORS, SmartImage } from '@cellebrite/saas-ui-common';
import Popup from '../Popup/Popup';
import { AddTagIcon } from '@cellebrite/saas-ui-icons';
import IconButton from '@mui/material/IconButton';
import TagsManager from '../TagsManager/TagsManager';
import { SECTION, socialPlatformsUserFriendlyDisplayName } from '../../constants/constants';
import { capitalizeFirstLetter } from '../../utils/formatters';
import { getSourceFromPlatform } from '../../utils/utils';
import TagsViewer from '../TagsViewer/TagsViewer';
import { GlobalContext } from '../../contexts/globalContext';

interface SquareAvatarProps {
    setTags?: any;
    avatar: any;
}

const SquareAvatar: React.FC<SquareAvatarProps> = ({ setTags, avatar }) => {
    const { reportData } = useContext(GlobalContext);
    const [showTag, setShowTag] = useState(false);
    const [tagOpen, setTagOpen] = useState(false);
    const [smartImageContainerStyles, setSmartImageContainerStyles] = useState({
        width: 80,
        height: 80,
        overflow: 'hidden',
    });

    return (
        <Box
            title={
                socialPlatformsUserFriendlyDisplayName.get(avatar.value.platform) ||
                capitalizeFirstLetter(avatar.value.platform)
            }
            sx={{ position: 'relative', width: 'fit-content' }}
            onMouseEnter={() => {
                setShowTag(true);
            }}
            onMouseLeave={() => {
                setShowTag(false);
            }}
            onClick={(event) => (event['stopParentCollapsePropagation'] = true)}
        >
            <Box
                sx={{
                    position: 'absolute',
                    right: '-2px',
                    bottom: '-2px',
                    zIndex: 1,
                    backgroundColor: COLORS.LIGHT,
                    padding: '4px',
                    borderRadius: '16px',
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <img src={getSourceFromPlatform(avatar.value?.platform)} />
            </Box>
            <SmartImage
                onLoad={() => setSmartImageContainerStyles({ width: 80, height: 80, overflow: null })}
                roundedCorners={true}
                containerStyles={smartImageContainerStyles}
                skeletonStyles={{ overflow: 'auto' }}
                src={avatar.value.source}
                title={capitalizeFirstLetter(avatar.value?.platform)}
                actionsElementStyles={{ m: 0 }}
                actionsElement={
                    (showTag || tagOpen) &&
                    reportData.collection?.taggable && (
                        <Popup
                            onToggle={() => {
                                setTagOpen(!tagOpen);
                            }}
                            actionElement={(handleOpen, anchorRef, open) => {
                                return (
                                    <Box
                                        ref={anchorRef}
                                        sx={{ p: 0.5, borderRadius: '8px', cursor: 'pointer', width: 32, height: 32 }}
                                        className="zoom-in-icon background-color-bd-opacity"
                                    >
                                        <IconButton
                                            sx={{
                                                'color': open ? COLORS.BRAND : COLORS.LIGHT,
                                                'backgroundColor': tagOpen ? COLORS.MUTED_DOUBLE_HOVER : null,
                                                '&:hover': {
                                                    backgroundColor: tagOpen
                                                        ? COLORS.MUTED_DOUBLE_HOVER
                                                        : COLORS.MUTED_HOVER,
                                                    color: open ? COLORS.BRAND : COLORS.MUTED_DARK,
                                                },
                                            }}
                                            disableRipple
                                            onClick={handleOpen}
                                        >
                                            <AddTagIcon />
                                        </IconButton>
                                    </Box>
                                );
                            }}
                            contentElement={(handleClose) => (
                                <Box>
                                    <TagsManager
                                        setTags={setTags}
                                        checkedTags={avatar.tags}
                                        section={SECTION.PERSONAL_INFO}
                                        elementId={avatar.value.id}
                                    />
                                </Box>
                            )}
                        />
                    )
                }
                labelElementStyles={
                    avatar.tags?.length
                        ? {
                              m: 0,
                              width: '32px !important',
                              height: 32,
                              borderRadius: '8px',
                              backgroundColor: 'rgba(18, 30, 40, 0.45)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                          }
                        : null
                }
                labelElement={reportData.collection?.taggable ? <TagsViewer checkedTags={avatar.tags} /> : null}
            />
        </Box>
    );
};

export default SquareAvatar;
